import * as React from "react";
import { useDispatch } from "react-redux";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Alert from "@material-ui/lab/Alert";

//icons
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ActionSend from "@material-ui/icons/Send";
import ActionClose from "@material-ui/icons/Close";

// jaza-soft
import { Button, showNotification } from "jazasoft";
import { minValue } from "@jazasoft/mui-table";

// local
import MuiTable from "../../../../../components/MuiTable";

import { isEmpty } from "../../../../../utils/helpers";
import { useDeepMemo, useMutation } from "../../../../../hooks";
import { InventoryStatus, QcStatus } from "../../../../../utils/types";
import { colorLightGreen, colorOrange, colorTomatoRed } from "../../../../../utils/colors";

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
          ? colorTomatoRed.dark
          : value === QcStatus.ON_HOLD
          ? colorOrange.dark
          : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxWidth: 1000
  },
  marginTop: {
    marginTop: "1.5em"
  },
  drawerHeader: {
    display: "flex",
    width: 540,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: "1.5em"
  },
  table: {
    "& td > div": {
      minWidth: 80
    }
  }
}));

const RollSplitDrawer = props => {
  const classes = useStyles();
  const { open, fabricItemIds = [], selectedRolls, rollFields, onDrawerClose, onRefresh } = props;

  const [mutate, {loading: saving}] = useMutation();
  const dispatch = useDispatch();

  const onSubmit = (rows, onSubmitComplete) => {
    if (!isEmpty(errorMessage)) {
      dispatch(showNotification(errorMessage, { type: "danger" }));
      onSubmitComplete && onSubmitComplete(rows);
      return;
    }

    if (rows.some(row => row.splitLength > row.length)) {
      dispatch(showNotification("Split Length cannot be more than Roll Length", { type: "danger" }));
      onSubmitComplete && onSubmitComplete(rows);
      return;
    }

    const options = {
      url: `rolls/splitting`,
      method: "put",
      data: rows,
      params: { action: "split", fabricItemIds: fabricItemIds.join(",") }
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`Rolls split successfully.`));
        onRefresh && onRefresh();
        onDrawerClose && onDrawerClose();
      },
      onFailure: () => {
        onSubmitComplete && onSubmitComplete(rows);
      }
    });
  };

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "rollNo", title: "Roll No" },
      { dataKey: "qcStatus", title: "QC Status", render: renderQcStatus },
      { dataKey: "status", title: "Status" },

      { dataKey: "width", title: "Cuttable\nWidth" },
      { dataKey: "warpShrPercent", title: "Shrinkage %\n(Length)" },
      { dataKey: "weftShrPercent", title: "Shrinkage %\n(Width)" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "skewness", title: "Skewness %" },
      { dataKey: "length", title: "Available\nLength" },
      {
        dataKey: "splitLength",
        title: "Split \nLength",
        inputType: "text-input",
        validate: [minValue(0)],
        disabled: row => row?.status === InventoryStatus.ISSUED || row?.status === InventoryStatus.PLANNED,
        options: { type: "number" }
      },
      { dataKey: "balLength", title: "Balance \nLength", render: (value, _, row) => (row.length || 0) - (row.splitLength || 0) }
    ];

    const fields = [...rollFields, "splitLength", "balLength", "status"];
    cols = cols.filter(c => fields.includes(c.dataKey));

    return cols;
  }, [rollFields]);

  const rows = useDeepMemo(() => {
    if (isEmpty(selectedRolls)) return [];
    return selectedRolls.map(e => ({
      ...e,
      splitLength: 0,
      status:
        e.invStatus === InventoryStatus.ISSUED
          ? InventoryStatus.ISSUED
          : (e.plannedInLay || 0) + (e.usedInLay || 0) + (e.usedInPart || 0) > 0
          ? InventoryStatus.PLANNED
          : InventoryStatus.NOT_PLANNED
    }));
  }, [selectedRolls]);

  const errorMessage = useDeepMemo(() => {
    let message;

    const isIssued = rows.some(roll => roll.status === InventoryStatus.ISSUED);
    const isPlanned = rows.some(roll => roll.status === InventoryStatus.PLANNED);
    if (isIssued) {
      message = `Issued rolls cannot be split`;
    } else if (isPlanned) {
      message = `Planned rolls cannot be split`;
    }
    return message;
  }, [rows]);

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  return (
    <div>
      <MuiDrawer classes={{ paper: classes.drawerPaper }} anchor="right" open={open} onClose={onDrawerClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
          <div>
            <Typography variant="h5">Split Roll</Typography>
          </div>
        </div>
        <Divider />

        <div className={classes.content}>
          {!isEmpty(errorMessage) && (
            <div>
              <Alert severity="error" variant="filled">
                <Typography variant="h6">{errorMessage}</Typography>
              </Alert>
            </div>
          )}

          <div className={classes.marginTop}>
            <MuiTable
              tableProps={{ className: classes.table }}
              columns={columns}
              rows={rows}
              editable={isEmpty(errorMessage)}
              editing={isEmpty(errorMessage)}
              onSubmit={(rows, _, onSubmitComplete) => onSubmit(rows, onSubmitComplete)}
              handleSubmitRef={handleSubmitRef}
              i18nKey="abc"
            />
          </div>

          <div style={{ display: "flex", flex: 1, flexGrow: 1 }}></div>

          {/* Footer */}
          <div className={classes.marginTop}>
            <Button label="Submit" variant="contained" color="primary" disabled={!isEmpty(errorMessage) || saving} onClick={event => ffHandleSubmit(event)}>
              <ActionSend />
            </Button>
            <Button label="Cancel" variant="outlined" color="primary" onClick={() => onDrawerClose()} style={{ marginLeft: "1em" }}>
              <ActionClose />
            </Button>
          </div>
        </div>
      </MuiDrawer>
    </div>
  );
};

export default RollSplitDrawer;
